import { Component, inject } from '@angular/core';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CustomAdapter } from '../../core/classes/custom-adapter';
import { CustomDateParserFormatter } from '../../core/classes/custom-date-parser-formatter';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../core/services/toast-service';
import { ToastMode } from '../../shared/models/toast/toast-mode.enum';
import { ReviewService } from '../../core/services/review.service';
import { ReviewsSearch } from '../../shared/models/reviews/reviews-search.model';
import { Paging } from '../../shared/models/paging/paging.model';
import { ProjectPerformanceReview } from '../../shared/models/pprs/project-performance-review.model';
import { UserSelectionsService } from '../../core/services/user-selections.service';
import { Company } from '../../shared/models/companies/company.model';

@Component({
  selector: 'app-embed-reviews',
  templateUrl: './embedreviews.component.html',
  styleUrl: './embedreviews.component.css',
  providers: [
    { provide: NgbDateAdapter, useClass: CustomAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
  ]
})
export class EmbedReviewsComponent {
  public isLoading: boolean = true;

  public id: string = '';

  public size: number = 10;

  public reviews: Paging<ProjectPerformanceReview> | null = null;

  public reviewsSearch: ReviewsSearch = {
    startDate: '',
    endDate: '',
    status: '',
    projectName: '',
    projectId: '',
    companyNumber: ''
  };

  public companies: { name: string, id: string }[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reviewService: ReviewService,
    private toastService: ToastService,
    private userSelectionsService: UserSelectionsService
  ) {
    // Load the user's selection
    this.reviewsSearch.startDate = this.userSelectionsService.get(this.userSelectionsService.keyReviewsStartDateSelection);
    this.reviewsSearch.endDate = this.userSelectionsService.get(this.userSelectionsService.keyReviewsEndDateSelection);
    this.reviewsSearch.status = this.userSelectionsService.get(this.userSelectionsService.keyReviewsStatusSelection);
    this.reviewsSearch.projectName = this.userSelectionsService.get(this.userSelectionsService.keyReviewsProjectNameSelection);
    this.reviewsSearch.projectId = this.userSelectionsService.get(this.userSelectionsService.keyReviewsProjectIdSelection);
    this.reviewsSearch.companyNumber = this.userSelectionsService.get(this.userSelectionsService.keyReviewsCompanyNumberSelection);
    this.size = Number(this.userSelectionsService.get(this.userSelectionsService.keyReviewsPageSizeSelection)) || this.size;
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.id = params['id'] ?? '';
      if (this.id == '') {
        // Todo: Need to redirect back to the project list
        return;
      }

      this.reviewsSearch = {
        startDate: '',
        endDate: '',
        status: '',
        projectName: '',
        projectId: this.id,
        companyNumber: ''
      };
      this.getReviews();
    });
  }

  public onSizeChange = () => this.getReviews();

  public onPageChange = (page: number) => this.getReviews(page);

  public onRowClick = (id: string) => this.router.navigate(['/reviews', id]);

  private companyId = (company: Company) => company.divisionId > 0
    ? `${company.companyId.toString().padStart(2, '0')}-${company.divisionId.toString().padStart(2, '0')}`
    : `${company.companyId.toString().padStart(2, '0')}`;

  private getReviews = (page: number = 1) => {
    // Save off the user's selection
    const startDate = new Date(this.reviewsSearch.startDate).toJSON() ?? '';
    const endDate = new Date(this.reviewsSearch.endDate).toJSON() ?? '';
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsStartDateSelection, startDate);
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsEndDateSelection, endDate);
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsStatusSelection, this.reviewsSearch.status);
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsProjectNameSelection, this.reviewsSearch.projectName);
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsProjectIdSelection, this.reviewsSearch.projectId);
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsCompanyNumberSelection, this.reviewsSearch.companyNumber);
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsPageSelection, page);
    this.userSelectionsService.set(this.userSelectionsService.keyReviewsPageSizeSelection, this.size);

    // Get the reviews
    this.isLoading = true;
    this.reviewService.getReviews(this.reviewsSearch, page, this.size).subscribe({
      next: (reviews) => {
        this.reviews = reviews;
      },
      error: (err) => {
        this.toastService.show('Error loading data.', ToastMode.Danger);
        console.log(err);
      }
    }).add(() => {
      this.isLoading = false;
    });
  }
}
